import React, { FC, useState, useEffect } from 'react';
import apiClient from '../api/axiosConfig';
import { AxiosError } from 'axios';
import './HasurGate.scss';

// ── No secrets here — password and answers are validated server-side only ──────

const STORAGE_BLOCKED = 'hasur_blocked_until'; // UI-only countdown (real block is server/IP)

// ── Quiz definition (labels only — correct values never stored client-side) ────
const QUESTIONS = [
  {
    id: 'q1',
    text: 'What is Enotik asking Hasmik about health?',
    options: [
      { value: 'a', label: 'Neck' },
      { value: 'b', label: 'Foot' },
      { value: 'c', label: 'Family member' },
      { value: 'd', label: 'Weather' },
    ],
  },
  {
    id: 'q2',
    text: 'What anime did you recently watch together?',
    options: [
      { value: 'a', label: 'AOT' },
      { value: 'b', label: 'Dandadan' },
      { value: 'c', label: 'Orb' },
      { value: 'd', label: 'Yaoi' },
    ],
  },
  {
    id: 'q3',
    text: 'What artist does Jazmik love the most?',
    options: [
      { value: 'a', label: '2Pac' },
      { value: 'b', label: 'The Queen' },
      { value: 'c', label: 'Marten Yorgantz' },
      { value: 'd', label: 'None of these' },
    ],
  },
];

// ── Props ─────────────────────────────────────────────────────────────────────
interface HasurGateProps {
  onEnter: (username: string, isHost: boolean, token: string) => void;
}

interface VerifyErrorResponse {
  error: string;
  attemptsLeft?: number;
  blockedUntil?: number | null;
}

type Step = 'blocked' | 'auth' | 'quiz' | 'entering';

// ── Component ─────────────────────────────────────────────────────────────────
const HasurGate: FC<HasurGateProps> = ({ onEnter }) => {
  const [step, setStep]             = useState<Step>('auth');
  const [username, setUsername]     = useState('');
  const [password, setPassword]     = useState('');
  const [authError, setAuthError]   = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [answers, setAnswers]       = useState<Record<string, string>>({});
  const [quizError, setQuizError]   = useState('');
  const [quizLoading, setQuizLoading] = useState(false);
  const [triesLeft, setTriesLeft]   = useState<number | null>(null);
  const [blockedUntil, setBlockedUntil] = useState(0);
  const [blockRemaining, setBlockRemaining] = useState('');

  // ── Restore UI countdown from localStorage (security block is server-side) ──
  useEffect(() => {
    const stored = parseInt(localStorage.getItem(STORAGE_BLOCKED) ?? '0', 10);
    if (stored && Date.now() < stored) {
      setBlockedUntil(stored);
      setStep('blocked');
    } else if (stored) {
      localStorage.removeItem(STORAGE_BLOCKED);
    }
  }, []);

  // ── Live countdown while blocked ─────────────────────────────────────────
  useEffect(() => {
    if (step !== 'blocked') return;
    const tick = (): void => {
      const remaining = blockedUntil - Date.now();
      if (remaining <= 0) {
        localStorage.removeItem(STORAGE_BLOCKED);
        setStep('auth');
        setTriesLeft(null);
        return;
      }
      const h = Math.floor(remaining / 3_600_000);
      const m = Math.floor((remaining % 3_600_000) / 60_000);
      const s = Math.floor((remaining % 60_000) / 1_000);
      setBlockRemaining(`${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [step, blockedUntil]);

  // ── Step 1: validate password server-side ────────────────────────────────
  const handleAuth = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!username.trim()) { setAuthError('Please enter your name.'); return; }
    setAuthLoading(true);
    setAuthError('');
    try {
      await apiClient.post('/api/hasur/auth', { password });
      setStep('quiz');
    } catch {
      setAuthError('Wrong access code. Try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  // ── Step 2: validate quiz answers server-side ────────────────────────────
  const handleQuiz = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (QUESTIONS.some((q) => !answers[q.id])) {
      setQuizError('Please answer all questions.');
      return;
    }
    setQuizLoading(true);
    setQuizError('');
    try {
      const res = await apiClient.post<{ token: string; hasHost: boolean }>(
        '/api/hasur/verify',
        { answers },
      );
      // Success — token lives in memory only, never persisted
      onEnter(username.trim(), !res.data.hasHost, res.data.token);
      setStep('entering');
    } catch (err) {
      const axErr = err as AxiosError<VerifyErrorResponse>;
      const data  = axErr.response?.data;
      if (data?.error === 'blocked' && data.blockedUntil) {
        localStorage.setItem(STORAGE_BLOCKED, String(data.blockedUntil));
        setBlockedUntil(data.blockedUntil);
        setStep('blocked');
      } else if (data?.error === 'wrong_answers') {
        setTriesLeft(data.attemptsLeft ?? null);
        if (data.blockedUntil) {
          localStorage.setItem(STORAGE_BLOCKED, String(data.blockedUntil));
          setBlockedUntil(data.blockedUntil);
          setStep('blocked');
        } else {
          setQuizError(
            `Incorrect answers. ${data.attemptsLeft ?? ''} attempt${data.attemptsLeft !== 1 ? 's' : ''} left.`,
          );
        }
      } else {
        setQuizError('Something went wrong. Please try again.');
      }
    } finally {
      setQuizLoading(false);
    }
  };

  // ── Blocked screen ────────────────────────────────────────────────────────
  if (step === 'blocked') {
    return (
      <div className="hasur-gate">
        <div className="hasur-card">
          <div className="hasur-icon">🔒</div>
          <h2 className="hasur-title">Access Blocked</h2>
          <p className="hasur-subtitle">Too many failed attempts. Come back in:</p>
          <div className="hasur-countdown">{blockRemaining}</div>
        </div>
      </div>
    );
  }

  // ── Entering screen ───────────────────────────────────────────────────────
  if (step === 'entering') {
    return (
      <div className="hasur-gate">
        <div className="hasur-card">
          <div className="hasur-icon">✨</div>
          <p className="hasur-entering">Opening the Secret Party…</p>
        </div>
      </div>
    );
  }

  // ── Quiz screen ───────────────────────────────────────────────────────────
  if (step === 'quiz') {
    return (
      <div className="hasur-gate">
        <div className="hasur-card hasur-card--wide">
          <div className="hasur-icon">🔑</div>
          <h2 className="hasur-title">Secret Questions</h2>
          <p className="hasur-subtitle">Answer correctly to enter the party.</p>
          <form className="hasur-form" onSubmit={handleQuiz}>
            {QUESTIONS.map((q, idx) => (
              <div key={q.id} className="hasur-question">
                <p className="hasur-q-text">
                  <span className="hasur-q-num">{idx + 1}.</span> {q.text}
                </p>
                <div className="hasur-options">
                  {q.options.map((opt) => (
                    <label
                      key={opt.value}
                      className={`hasur-option${answers[q.id] === opt.value ? ' selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        value={opt.value}
                        checked={answers[q.id] === opt.value}
                        onChange={() =>
                          setAnswers((prev) => ({ ...prev, [q.id]: opt.value }))
                        }
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            {quizError && <div className="hasur-error">{quizError}</div>}
            {triesLeft !== null && !quizError && (
              <div className="hasur-tries">
                Attempts remaining: <strong>{triesLeft}</strong>
              </div>
            )}
            <button type="submit" className="hasur-btn" disabled={quizLoading}>
              {quizLoading ? 'Checking…' : 'Confirm Answers'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Auth screen ───────────────────────────────────────────────────────────
  return (
    <div className="hasur-gate">
      <div className="hasur-card">
        <div className="hasur-icon">🔐</div>
        <h2 className="hasur-title">Secret Entrance</h2>
        <p className="hasur-subtitle">
          Enter your name and the access code to continue.
        </p>
        <form className="hasur-form" onSubmit={handleAuth}>
          <div className="hasur-field">
            <label htmlFor="hg-name">Your Name</label>
            <input
              id="hg-name"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your name"
              autoFocus
              maxLength={32}
              disabled={authLoading}
            />
          </div>
          <div className="hasur-field">
            <label htmlFor="hg-code">Access Code</label>
            <input
              id="hg-code"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••••••••••"
              disabled={authLoading}
            />
          </div>
          {authError && <div className="hasur-error">{authError}</div>}
          {triesLeft !== null && (
            <div className="hasur-tries">
              Quiz attempts remaining: <strong>{triesLeft}</strong>
            </div>
          )}
          <button type="submit" className="hasur-btn" disabled={authLoading}>
            {authLoading ? 'Checking…' : 'Enter'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default HasurGate;
